import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { FiMenu, FiX, FiChevronDown } from 'react-icons/fi'
import { useCategories } from '../../hooks/useFetch'
import { categoryService } from '../../services/category.service'
import { APP_CONFIG } from '../../utils/constants'

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isCatalogOpen, setIsCatalogOpen] = useState(false)
  const dropdownRef = useRef(null)
  const mobileDropdownRef = useRef(null)
    
  const { categories, loading: loadingCategories } = useCategories(
    () => categoryService.getCategories()
  )

  // Efecto para cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Verificar tanto dropdown desktop como móvil
      const desktopDropdown = dropdownRef.current
      const mobileDropdown = mobileDropdownRef.current
      
      const clickedOutsideDesktop = !desktopDropdown || !desktopDropdown.contains(event.target)
      const clickedOutsideMobile = !mobileDropdown || !mobileDropdown.contains(event.target)
      
      if (isCatalogOpen && clickedOutsideDesktop && clickedOutsideMobile) {
        setIsCatalogOpen(false)
      }
    }

    if (isCatalogOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isCatalogOpen])
    
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen)
    if (isCatalogOpen) setIsCatalogOpen(false)
  }
    
  const toggleCatalog = (e) => {
    e.preventDefault()
    setIsCatalogOpen(!isCatalogOpen)
  }
    
  const closeMenus = () => {
    setIsMenuOpen(false)
    setIsCatalogOpen(false)
  }
    
  const menuItems = [
    {
      title: 'Inicio',
      to: '/',
      action: closeMenus
    },
    {
      title: 'Catálogo',
      hasDropdown: true,
      action: toggleCatalog,
      items: categories || []
    },
    {
      title: 'XV años',
      to: '/categoria/Coronas',
      action: closeMenus
    },
    {
      title: 'Reinas',
      to: '/categoria/Coronas de Reina',
      action: closeMenus
    },
    {
      title: 'Contacto',
      to: '/contacto',
      action: closeMenus
    }
  ]
    
  return (
    <header className="bg-white shadow-sm border-b border-gray-100 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Mobile Layout - Sin cambios */}
        <div className="flex justify-between items-center h-16 md:hidden">
          {/* Logo */}
          <div className="flex items-center">
            <Link 
              to="/" 
              className="text-2xl font-decorative text-primary-dark hover:text-primary-text transition-colors duration-300"
              onClick={closeMenus}
            >
              {APP_CONFIG.NAME}
            </Link>
          </div>
                    
          {/* Mobile menu button */}
          <button
            onClick={toggleMenu}
            className="p-2 rounded-md text-primary-text hover:text-primary-dark hover:bg-primary-section transition-colors duration-300"
          >
            {isMenuOpen ? (
              <FiX className="h-6 w-6" />
            ) : (
              <FiMenu className="h-6 w-6" />
            )}
          </button>
        </div>

        {/* Desktop Layout - Centrado vertical */}
        <div className="hidden md:flex md:flex-col md:items-center md:py-4">
          {/* Logo centrado arriba */}
          <div className="mb-4">
            <Link 
              to="/" 
              className="text-3xl font-decorative text-primary-dark hover:text-primary-text transition-colors duration-300"
              onClick={closeMenus}
            >
              {APP_CONFIG.NAME}
            </Link>
          </div>
                    
          {/* Navigation centrada abajo */}
          <nav className="flex items-center space-x-8">
            {menuItems.map((item, index) => (
              <div key={index} className="relative group" ref={item.hasDropdown ? dropdownRef : null}>
                {item.hasDropdown ? (
                  <div>
                    <button
                      onClick={item.action}
                      className="flex items-center text-primary-text hover:text-primary-dark transition-colors duration-300"
                    >
                      {item.title}
                      <FiChevronDown 
                        className={`ml-1 h-4 w-4 transition-transform duration-300 ${isCatalogOpen ? 'rotate-180' : ''}`} 
                      />
                    </button>
                                        
                    {/* Dropdown Menu */}
                    {isCatalogOpen && (
                      <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-100 py-2 z-50">
                        {loadingCategories ? (
                          <div className="px-4 py-2 text-primary-gray">
                            Cargando categorías...
                          </div>
                        ) : (
                          item.items.map((category, catIndex) => (
                            <Link
                              key={catIndex}
                              to={`/categoria/${category.category}`}
                              className="block px-4 py-2 text-primary-text hover:bg-primary-section hover:text-primary-dark transition-colors duration-300"
                              onClick={closeMenus}
                            >
                              {category.category}
                            </Link>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.to}
                    onClick={item.action}
                    className="text-primary-text hover:text-primary-dark transition-colors duration-300"
                  >
                    {item.title}
                  </Link>
                )}
              </div>
            ))}
          </nav>
        </div>
                
        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-gray-100">
            <div className="py-2 space-y-1">
              {menuItems.map((item, index) => (
                <div key={index} ref={item.hasDropdown ? mobileDropdownRef : null}>
                  {item.hasDropdown ? (
                    <div>
                      <button
                        onClick={item.action}
                        className="w-full flex items-center justify-between px-3 py-2 text-left text-primary-text hover:text-primary-dark hover:bg-primary-section transition-colors duration-300"
                      >
                        {item.title}
                        <FiChevronDown 
                          className={`h-4 w-4 transition-transform duration-300 ${isCatalogOpen ? 'rotate-180' : ''}`}
                        />
                      </button>
                                            
                      {isCatalogOpen && (
                        <div className="bg-primary-section">
                          {loadingCategories ? (
                            <div className="px-6 py-2 text-primary-gray text-sm">
                              Cargando categorías...
                            </div>
                          ) : (
                            item.items.map((category, catIndex) => (
                              <Link
                                key={catIndex}
                                to={`/categoria/${category.category}`}
                                className="block px-6 py-2 text-sm text-primary-text hover:text-primary-dark transition-colors duration-300"
                                onClick={closeMenus}
                              >
                                {category.category}
                              </Link>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={item.to}
                      onClick={item.action}
                      className="block px-3 py-2 text-primary-text hover:text-primary-dark hover:bg-primary-section transition-colors duration-300"
                    >
                      {item.title}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  )
}

export default Header